/**
 * DELIVERY — AT THE CUSTOMER'S GATE (item 6).
 *
 * Every cylinder on the load gets an outcome, one at a time: delivered,
 * refused, short, not attempted, cancelled. A cylinder that was not delivered
 * must also say WHERE IT IS NOW and whose responsibility it is — those are the
 * two facts a shortage investigation starts from, and a screen that lets a
 * driver skip them is the reason a cylinder is never found.
 *
 * Confirming is retry-sensitive: the proof is recorded once, and a repeated
 * submit after a timeout carries the same key (DEF-088).
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, useRun, go } from '../hooks.js';
import { Banner, Button, Card, Count, Loading, Pill, Register, Select, Serial, Text, TextArea, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { NextStep } from '../next.js';
import { count, date, days, today, words } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('delivery')!.messages;
type Outcome = 'delivered' | 'refused' | 'shortage' | 'not_attempted' | 'cancelled';

export function Delivery({ id, session }: { id: string | null; session: Session }) {
  if (id) return <ConfirmOne challanId={Number(id)} session={session} />;
  return <OutForDelivery />;
}

/**
 * THE LOADS WAITING FOR A DELIVERY TO BE CONFIRMED.
 *
 * The owner's third priority: *"Moving a shipment from out for delivery to delivered is
 * difficult to navigate."* Three things were wrong with this list and none of them was the
 * absence of a route — there were four routes. It was that the list did not SAY what it was
 * for, carried no action, and could not be searched, so finding one load among fifty meant
 * reading fifty rows (R20261005-06).
 */
function OutForDelivery() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const asked = useAsk(() => api.view('challans.list', {
    type: 'dispatch', journey: 'departed', page, pageSize: 50, ...(q.trim() ? { q: q.trim() } : {}),
  }), [page, q], MESSAGES);
  const columns: readonly Column<{ challanId: number; number: string | null; customerName: string; siteName: string; departedOn: string | null; lineCount: number }>[] = [
    { key: 'number', head: 'Challan', cell: (r) => <Serial>{r.number}</Serial> },
    { key: 'customer', head: 'Customer', cell: (r) => <><strong>{r.customerName}</strong><div className="muted">{r.siteName}</div></> },
    { key: 'left', head: 'Left', cell: (r) => date(r.departedOn) },
    { key: 'cyl', head: 'Cylinders', right: true, cell: (r) => <Count n={r.lineCount} /> },
    // THE ACTION ON THE ROW, in the words the stage uses and the button repeats. Opening the
    // row does the same thing; the button is there because an operator looking for something
    // to do should not have to discover that a row is clickable.
    { key: 'do', head: '', cell: (r) => (
        <Button small kind="primary" onClick={() => go(`#/delivery/${r.challanId}`)}>Confirm delivery</Button>) },
  ];
  return (
    <Page title="Out for delivery" sub="Loads that have left and have no proof of delivery yet. Open one to confirm it, or to say what went wrong.">
      <Card>
        <div className="filters">
          <label htmlFor="q">Search</label>
          <input id="q" type="text" value={q} placeholder="Challan number or customer"
                 onChange={(e: { target: { value: string } }) => { setQ(e.target.value); setPage(1); }} />
        </div>
        {asked.loading ? <Loading what="loads out for delivery" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : (<>
              <Register columns={columns} rows={asked.data!.rows}
                        empty={q.trim() ? 'No load out for delivery matches that.' : 'Every load that left is accounted for.'}
                        onOpen={(r) => go(`#/delivery/${r.challanId}`)} />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>
    </Page>
  );
}

/**
 * RESOLVING A SHORTAGE. The cylinder turns up at the plant, or the customer
 * says he has had it all along, or nobody can find it and it is written off.
 * Found WITH THE CUSTOMER is a delivery that happened late, so it asks for the
 * date and who took it — otherwise the holding is charged from the wrong day.
 * Recording it is retry-sensitive: the same answer must not be recorded twice
 * (DEF-088).
 */
function ResolveShortage({ challanId, line, onDone }: {
  challanId: number;
  line: { cylinderId: number; internalId: string; serialNo: string; gasService: string };
  onDone: () => void;
}) {
  const [outcome, setOutcome] = useState('');
  const [resolvedOn, setResolvedOn] = useState(today());
  const [reason, setReason] = useState('');
  const [deliveredOn, setDeliveredOn] = useState(today());
  const [receiverName, setReceiverName] = useState('');
  const [acknowledgement, setAcknowledgement] = useState('signature');
  const runner = useRun(MESSAGES);
  const withCustomer = outcome === 'found_with_customer';

  async function submit() {
    const ok = await runner.run((retryKey) => api.command('dispatch.resolveShortage', {
      challanId, cylinderId: line.cylinderId, resolvedOn,
      outcome: outcome as 'found_at_plant' | 'found_with_customer' | 'declared_lost',
      reason: reason.trim(),
      ...(withCustomer ? { deliveredOn } : {}),
      ...(withCustomer && receiverName.trim() ? { receiverName: receiverName.trim() } : {}),
      ...(withCustomer && acknowledgement ? { acknowledgement: acknowledgement as 'signature' | 'otp' | 'photograph' | 'recorded_confirmation' } : {}),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) onDone();
  }

  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 12 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
        <Serial>{line.internalId}</Serial>
        <span className="muted">{line.serialNo} · {words(line.gasService)}</span>
      </div>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} {...(runner.error.reference ? { reference: runner.error.reference } : {})} /> : null}
      <div className="grid two">
        <div>
          <Select name={`resolve-${line.cylinderId}`} label="What was found out" value={outcome} onChange={setOutcome} placeholder="Choose what happened"
                  options={[{ value: 'found_at_plant', label: 'Found at the plant — it never left' },
                            { value: 'found_with_customer', label: 'The customer had it all along' },
                            { value: 'declared_lost', label: 'Nobody can find it — declared lost' }]} />
          <Text name={`resolved-on-${line.cylinderId}`} label="Settled on" type="date" value={resolvedOn} onChange={setResolvedOn} required />
        </div>
        <div>
          {withCustomer ? (
            <>
              <Text name={`late-delivered-${line.cylinderId}`} label="Actually delivered on" type="date" value={deliveredOn} onChange={setDeliveredOn} required
                    help="holding is charged from this day, not from today" />
              <Text name={`late-receiver-${line.cylinderId}`} label="Received by" value={receiverName} onChange={setReceiverName} />
              <Select name={`late-ack-${line.cylinderId}`} label="Proof" value={acknowledgement} onChange={setAcknowledgement}
                      options={[{ value: 'signature', label: 'Signature' }, { value: 'otp', label: 'OTP' }, { value: 'photograph', label: 'Photograph' }, { value: 'recorded_confirmation', label: 'Recorded confirmation' }]} />
            </>
          ) : null}
          <TextArea name={`resolve-why-${line.cylinderId}`} label="What happened" value={reason} onChange={setReason} />
        </div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !outcome || !reason.trim()}>
        {runner.busy ? 'Recording…' : 'Settle this cylinder'}
      </Button>
    </div>
  );
}

interface LineDecision { outcome: Outcome; nowAt: string; responsibility: string; reason: string }

/** 10 Oct — `embedded`: the step on the challan's own page, without a page frame. */
export function ConfirmOne({ challanId, session, embedded, onChanged }: { challanId: number; session: Session; embedded?: boolean; onChanged?: () => void }) {
  const asked = useAsk(() => api.view('challans.get', { challanId }), [challanId], MESSAGES);
  const runner = useRun(MESSAGES);
  const [deliveredOn, setDeliveredOn] = useState(today());
  const [receiverName, setReceiverName] = useState('');
  const [receiverRole, setReceiverRole] = useState('');
  const [acknowledgement, setAcknowledgement] = useState('signature');
  const [acknowledgementRef, setAcknowledgementRef] = useState('');
  const [note, setNote] = useState('');
  const [decisions, setDecisions] = useState<Record<number, LineDecision>>({});

  if (asked.loading) return embedded ? <Loading what="the load" /> : <Page title="Delivery"><Loading what="the load" /></Page>;
  if (asked.error) return embedded ? <Banner kind="error" title={asked.error.title} /> : <Page title="Delivery"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Page>;
  const d = asked.data!;
  const c = d.challan;
  const open = d.lines.filter((l) => !l.terminal);
  // A shortage is recorded at the gate and RESOLVED later — that is the whole
  // point of recording it rather than guessing on the spot (D256).
  const shortages = d.lines.filter((l) => l.deliveryOutcome === 'shortage' && l.nowAt === 'unaccounted');

  function decide(cylinderId: number, patch: Partial<LineDecision>) {
    setDecisions((old) => ({ ...old, [cylinderId]: { outcome: 'delivered', nowAt: '', responsibility: '', reason: '', ...old[cylinderId], ...patch } }));
  }
  // Whether this is an ordinary delivery or one with a problem on it — which decides what the
  // button may honestly claim to be doing.
  const allDelivered = open.every((l) => decisionFor(l.cylinderId).outcome === 'delivered');

  function decisionFor(cylinderId: number): LineDecision {
    return decisions[cylinderId] ?? { outcome: 'delivered', nowAt: '', responsibility: '', reason: '' };
  }

  async function confirm() {
    const ok = await runner.run((retryKey) => api.command('dispatch.confirmDelivery', {
      challanId, deliveredOn,
      lines: open.map((l) => {
        const dec = decisionFor(l.cylinderId);
        return {
          cylinderId: l.cylinderId, outcome: dec.outcome,
          ...(dec.outcome !== 'delivered' && dec.nowAt ? { nowAt: dec.nowAt as 'plant' | 'vehicle' | 'unaccounted' } : {}),
          ...(dec.outcome !== 'delivered' && dec.responsibility ? { responsibility: dec.responsibility as 'customer' | 'company' | 'unresolved' } : {}),
          ...(dec.outcome !== 'delivered' && dec.reason.trim() ? { reason: dec.reason.trim() } : {}),
        };
      }),
      ...(receiverName.trim() ? { receiverName: receiverName.trim() } : {}),
      ...(receiverRole.trim() ? { receiverRole: receiverRole.trim() } : {}),
      ...(acknowledgement ? { acknowledgement: acknowledgement as 'signature' | 'otp' | 'photograph' | 'recorded_confirmation' } : {}),
      ...(acknowledgementRef.trim() ? { acknowledgementRef: acknowledgementRef.trim() } : {}),
      ...(note.trim() ? { exceptionNote: note.trim() } : {}),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) asked.reload(); onChanged?.();
  }

  const body = (
    <>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} {...(runner.error.reference ? { reference: runner.error.reference } : {})} /> : null}

      {d.proofs.length ? (
        <Card title="Proof already recorded">
          {d.proofs.map((p) => (
            <div className="detail-grid" key={p.proofId} style={{ marginBottom: 10 }}>
              <div><div className="k">Delivered</div><div className="v">{date(p.deliveredOn)}</div></div>
              <div><div className="k">Received by</div><div className="v">{p.receiverName ?? '—'}{p.receiverRole ? ` · ${p.receiverRole}` : ''}</div></div>
              <div><div className="k">Proof</div><div className="v">{words(p.acknowledgement)}{p.acknowledgementRef ? ` · ${p.acknowledgementRef}` : ''}</div></div>
              {p.provisional ? <div><div className="k">Provisional</div><div className="v"><Pill tone="empty">captured offline</Pill></div></div> : null}
            </div>
          ))}
        </Card>
      ) : null}

      {/*
        * R20261005-08 — TWO QUESTIONS, NOT ONE.
        *
        * This branched on `open.length === 0` alone and announced "Nothing is waiting on this
        * delivery" — directly above the card listing the cylinders nobody could account for. A
        * shortage IS an outcome, so "every line has been decided" and "nothing is outstanding"
        * are different questions, and answering the second with the first is how a screen comes
        * to contradict itself on one page.
        *
        * The order matters too: the unresolved thing is said FIRST, because a person who reads
        * "all done" stops reading.
        */}
      {open.length > 0 ? (
        <NextStep title="Say what happened to each cylinder, and who took them."
                  detail="A cylinder that was not delivered must say where it is now and whose responsibility it is." />
      ) : shortages.length > 0 ? (
        <NextStep kind="warn"
                  title={`Every cylinder has an outcome, and ${count(shortages.length)} of them cannot be accounted for.`}
                  detail="This delivery is not finished. Each one is either found — at the plant or with the customer — or declared lost, and until then it stays on this page and on the customer's account."
                  secondary={{ label: 'The customer\'s account', href: `#/account/${c.customerId}` }} />
      ) : (
        <NextStep kind="good" title="This delivery is complete."
                  detail="Every cylinder was accounted for. The empties come back on a return challan; the holding is on the customer's account."
                  {...(can(session, 'challan.create') ? { action: { label: 'Record a return', href: '#/returns/new' } } : {})}
                  secondary={{ label: 'The customer\'s account', href: `#/account/${c.customerId}` }} />
      )}
      {open.length === 0 ? null : (
        <div className="grid two">
          <Card title="What happened to each cylinder" sub="a cylinder that was not delivered says where it is and whose responsibility it is">
            {open.map((l) => {
              const dec = decisionFor(l.cylinderId);
              return (
                <div key={l.cylinderId} style={{ borderBottom: '1px solid var(--border)', paddingBottom: 10, marginBottom: 10 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
                    <Serial>{l.internalId}</Serial>
                    <span className="muted">{l.serialNo} · {words(l.gasService)}</span>
                  </div>
                  <Select name={`outcome-${l.cylinderId}`} label="Outcome" value={dec.outcome}
                          onChange={(v) => decide(l.cylinderId, { outcome: v as Outcome })}
                          options={[{ value: 'delivered', label: 'Delivered' }, { value: 'refused', label: 'Refused by the customer' },
                                    { value: 'shortage', label: 'Short — not on the lorry' }, { value: 'not_attempted', label: 'Not attempted' },
                                    { value: 'cancelled', label: 'Cancelled' }]} />
                  {dec.outcome !== 'delivered' ? (
                    <>
                      <Select name={`where-${l.cylinderId}`} label="Where is it now?" value={dec.nowAt} onChange={(v) => decide(l.cylinderId, { nowAt: v })}
                              placeholder="Say where it is"
                              options={[{ value: 'plant', label: 'Back at the plant' }, { value: 'vehicle', label: 'Still on the vehicle' }, { value: 'unaccounted', label: 'Unaccounted for' }]} />
                      <Select name={`who-${l.cylinderId}`} label="Whose responsibility?" value={dec.responsibility} onChange={(v) => decide(l.cylinderId, { responsibility: v })}
                              placeholder="Say whose"
                              options={[{ value: 'customer', label: "The customer's" }, { value: 'company', label: 'Ours' }, { value: 'unresolved', label: 'Not settled yet' }]} />
                      <TextArea name={`why-${l.cylinderId}`} label="What happened" value={dec.reason} onChange={(v) => decide(l.cylinderId, { reason: v })} />
                    </>
                  ) : null}
                </div>
              );
            })}
          </Card>

          <Card title="Who took them">
            <Text name="delivered-on" label="Delivered on" type="date" value={deliveredOn} onChange={setDeliveredOn} required />
            <Text name="receiver" label="Received by" value={receiverName} onChange={setReceiverName} />
            <Text name="receiver-role" label="Their role" value={receiverRole} onChange={setReceiverRole} />
            <Select name="ack" label="Proof" value={acknowledgement} onChange={setAcknowledgement}
                    options={[{ value: 'signature', label: 'Signature' }, { value: 'otp', label: 'OTP' }, { value: 'photograph', label: 'Photograph' }, { value: 'recorded_confirmation', label: 'Recorded confirmation' }]} />
            <Text name="ack-ref" label="Proof reference" value={acknowledgementRef} onChange={setAcknowledgementRef} />
            <TextArea name="note" label="Note" value={note} onChange={setNote} />
            <Button kind="primary" wide disabled={!can(session, 'delivery.confirm') || runner.busy} onClick={() => { void confirm(); }}>
              {/* R20261005-07 — THE SAME WORDS AS THE STAGE AND THE LINK THAT GOT HERE. "Record
                  the outcome of 4 cylinder(s)" described the mechanism; the operator came here
                  to confirm a delivery, and says so everywhere else. When something went wrong
                  on the load the button says THAT instead, because pressing "Confirm delivery"
                  over a refusal or a shortage would be the screen lying about what it did. */}
              {runner.busy ? 'Recording…'
                : allDelivered ? `Confirm delivery of ${count(open.length)} cylinder${open.length === 1 ? '' : 's'}`
                : 'Record what happened to this load'}
            </Button>
          </Card>
        </div>
      )}

      {shortages.length ? (
        <Card title="Shortages still open" sub="a cylinder nobody can account for is an open question until somebody answers it">
          <Banner kind="warn" title={`${count(shortages.length)} cylinder(s) from this load are unaccounted for.`}
                  detail="Every one of them is either found — at the plant or with the customer — or declared lost. Until then it keeps appearing here." />
          {shortages.map((l) => (
            <div key={l.cylinderId}>
              <ResolveShortage challanId={challanId} line={l} onDone={() => asked.reload()} />
            </div>
          ))}
        </Card>
      ) : null}

      {embedded ? null : <Card title="Papers" sub="printed when the truck left, and never redrawn">
        {d.documents.length === 0 ? <p className="muted">No document has been issued for this load yet.</p> : (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {d.documents.map((doc) => (
              <li key={doc.id}>
                <a href={api.documentUrl(doc.id)} target="_blank" rel="noreferrer">{words(doc.kind)} {doc.number}</a>
                <span className="muted"> · version {doc.version}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>}

      <Card title="Everything on this load">
        <Register
          columns={[
            { key: 'cyl', head: 'Cylinder', cell: (l: { internalId: string }) => <Serial>{l.internalId}</Serial> },
            { key: 'gas', head: 'Gas', cell: (l: { gasService: string }) => words(l.gasService) },
            { key: 'outcome', head: 'Outcome', cell: (l: { deliveryOutcome: string | null }) => l.deliveryOutcome ? <Pill>{l.deliveryOutcome}</Pill> : <span className="muted">waiting</span> },
            { key: 'where', head: 'Now at', cell: (l: { nowAt: string | null }) => words(l.nowAt) },
            { key: 'who', head: 'Responsibility', cell: (l: { responsibility: string | null }) => words(l.responsibility) },
            { key: 'attempts', head: 'Attempts', right: true, cell: (l: { attempts: number }) => l.attempts > 1 ? count(l.attempts) : <span className="muted">—</span> },
          ]}
          rows={d.lines} empty="Nothing is on this load." />
      </Card>
    </>
  );
  if (embedded) return body;
  return (
    <Page title={`Delivery of ${c.number ?? `the load for ${c.customerName}`}`} sub={`${c.customerName} · ${c.siteName} · left ${date(c.departedOn)}`}>
      {body}
    </Page>
  );
}
